import { useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';

import { Avatar, AvatarFallback, AvatarImage } from '../../../components/ui/avatar';
import { Button } from '../../../components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../../components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/popover';
import { cn } from '../../../components/ui/utils';
import { plural } from '../lib/format-metrics-ru';
import { PlatformBadge, type Platform } from '../ui/PlatformBadge';

type AccountItem = {
  id: string;
  username?: string;
  name: string;
  profilePicture?: string;
  /** Если задана — рядом с именем значок площадки (нужен, когда в списке смесь). */
  platform?: Platform;
};

type AccountMultiSelectProps = {
  accounts: AccountItem[];
  value: string[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
};

function AccountRow({ account }: { account: AccountItem }): JSX.Element {
  const displayName = account.username || account.name;
  const initials = displayName.slice(0, 2).toUpperCase();
  return (
    <div className="flex items-center gap-2 min-w-0">
      <Avatar className="size-6 shrink-0">
        <AvatarImage src={account.profilePicture} alt={displayName} />
        <AvatarFallback className="text-[10px] font-semibold">{initials}</AvatarFallback>
      </Avatar>
      <span className="font-medium truncate">@{displayName}</span>
      {account.platform && <PlatformBadge platform={account.platform} />}
    </div>
  );
}

export function AccountMultiSelect({
  accounts,
  value,
  onChange,
  disabled,
}: AccountMultiSelectProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const isEmpty = accounts.length === 0;
  const selectedAccounts = accounts.filter(a => value.includes(a.id));

  function toggleAccount(accountId: string): void {
    if (value.includes(accountId)) {
      onChange(value.filter(id => id !== accountId));
    } else {
      onChange([...value, accountId]);
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled || isEmpty}
          className="min-w-[260px] h-10 justify-between font-normal"
        >
          {selectedAccounts.length === 0 ? (
            <span className="text-muted-foreground">
              {isEmpty ? 'Нет подключённых аккаунтов' : 'Выберите аккаунты'}
            </span>
          ) : selectedAccounts.length === 1 ? (
            <AccountRow account={selectedAccounts[0]} />
          ) : (
            <span>
              {/* Было «5 аккаунта выбрано» — число склонялось только для 2–4. */}
              {selectedAccounts.length}{' '}
              {plural(selectedAccounts.length, 'аккаунт выбран', 'аккаунта выбрано', 'аккаунтов выбрано')}
            </span>
          )}
          <ChevronsUpDown className="size-4 text-muted-foreground shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command
          filter={(itemValue, search) =>
            itemValue.toLowerCase().includes(search.toLowerCase()) ? 1 : 0
          }
        >
          <CommandInput placeholder="Поиск аккаунта…" />
          {/* Когда выбраны все 45, снять лишние по одному невозможно —
              поэтому «Снять все», а на строке «только» для выбора одного. */}
          <div className="flex items-center justify-between gap-2 border-b px-2 py-1.5">
            <span className="text-xs text-muted-foreground">
              Выбрано {value.length} из {accounts.length}
            </span>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs"
                disabled={value.length === accounts.length}
                onClick={() => onChange(accounts.map(a => a.id))}
              >
                Выбрать все
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs"
                disabled={value.length === 0}
                onClick={() => onChange([])}
              >
                Снять все
              </Button>
            </div>
          </div>
          <CommandList>
            <CommandEmpty>Ничего не найдено</CommandEmpty>
            {accounts.map(account => {
              const displayName = account.username || account.name;
              const isSelected = value.includes(account.id);
              const isOnlyOne = isSelected && value.length === 1;
              return (
                <CommandItem
                  key={account.id}
                  value={`${displayName} ${account.name}`}
                  onSelect={() => toggleAccount(account.id)}
                  className="group"
                >
                  <div className="flex items-center justify-between gap-2 w-full min-w-0">
                    <AccountRow account={account} />
                    <span className="flex shrink-0 items-center gap-1">
                      {!isOnlyOne && (
                        <span
                          role="button"
                          tabIndex={-1}
                          className="rounded px-1.5 py-0.5 text-xs text-muted-foreground opacity-0 transition-opacity hover:bg-accent hover:text-foreground group-hover:opacity-100 group-data-[selected=true]:opacity-100"
                          onClick={event => {
                            event.stopPropagation();
                            onChange([account.id]);
                          }}
                        >
                          только
                        </span>
                      )}
                      <Check
                        className={cn('size-4 shrink-0', isSelected ? 'opacity-100' : 'opacity-0')}
                      />
                    </span>
                  </div>
                </CommandItem>
              );
            })}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
